import React, { Fragment, useEffect, useMemo, FC, useState, Suspense, useRef, useCallback } from 'react';
import { get, map, isEqual } from 'lodash';
import {
  Box,
  Spinner,
  SpinnerSize,
  useModalState,
  IconSize,
  Icon,
  ButtonAppearance,
  ButtonSize,
  SegmentedControl,
  SegmentedControlOption,
  SegmentedControlSize,
  Text,
  Heading,
  Pagination,
} from '@bechtel/gds';
import {
  WorkPackage,
  PagingInput,
  PersonaType,
  WorkPackageFilterInput,
  MilestoneFilterStatusFlag,
  WorkPackageList,
  WorkPackageOwnerFilterByTerm,
  WorkPackageSortByTerm,
  MilestoneType,
  CommodityType,
  WorkPackageFilterByTerm,
  WorkPackageSearchByEnum,
  OwnershipType,
} from '@bechtel/types';
import { Drawer } from './drawer';
import { initialDrawerValues as defaultValues, DrawerValuesProps, SortDirection } from './drawer/drawer-state';
import { ListItem } from './item';
import {
  ListStyled,
  WorkPackageListContainerStyled,
  SearchContainerStyled,
  PaginationContainerStyled,
  SearchCanvasButtonStyled,
} from './styled';
import { ListSubHeadingStyled } from '../styled';
import {
  usePlanning,
  PlanningActionType,
  useDebounce,
  ErrorRetry,
  useSessionStorage,
  SearchField,
  CacheKeys,
  useAppUser,
  AllSuperintendentId,
  AllGeneralForemanId,
  AllForemanId,
  UnassignedForemanId,
  UnassignedGeneralForemanId,
  UnassignedSuperintendentId,
} from '@bechtel/client-common';
import { GET_WORKPACKAGES } from '@bechtel/client-gql';
//@ts-ignore declaration not recognizing svg
import { ReactComponent as FilterIcon } from '@bechtel/client-assets/dist/images/filter.svg';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { PlanningWorkPackageSlider, WorkPackagesListProps, planningWorkPackageLimit } from './types';
import { Trans, t } from '@lingui/macro';

export const WorkPackagesList: FC<WorkPackagesListProps> = ({ setDrawerStatus }) => {
  const { user } = useAppUser();
  const {
    planningDispatch,
    selectedSuperintendent,
    selectedGeneralForeman,
    selectedForeman,
    isReplan,
    resetWorkPackagePager,
  } = usePlanning();
  const [searchQuery, setSearchQuery] = useState('');
  const [planningWorkPackageSlider, setPlanningWorkPackageSlider] = useState<PlanningWorkPackageSlider>(
    PlanningWorkPackageSlider.Single
  );
  const [planningWpListCursor, setCursor] = useState<number>(1);
  const debouncedSearchQuery = useDebounce(searchQuery?.trim(), 500);
  const { close, toggle, visible } = useModalState(false);

  const wpDefaultDrawerValues =
    user &&
    user.crewMember &&
    user.crewMember.persona &&
    (user.crewMember.persona === PersonaType.FOREMAN ||
      user.crewMember.persona === PersonaType.GENERAL_FOREMAN ||
      (user.crewMember.persona === PersonaType.SUPERINTENDENT && user.crewMember.id === selectedSuperintendent?.id))
      ? defaultValues
      : { ...defaultValues, searchScope: false };

  const persistedDrawerValues = useRef(wpDefaultDrawerValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<DrawerValuesProps>(
    CacheKeys.PlanningWorkPackageDrawerValues,
    persistedDrawerValues.current || wpDefaultDrawerValues
  );

  const closeDrawer = () => {
    setTimeout(function() {
      close();
      setDrawerStatus(!visible);
    }, 0);
  };
  const [loadWorkpackages, { loading, data, error, refetch, called, networkStatus }] = useLazyQuery<
    { workPackages: WorkPackageList },
    {
      filter: WorkPackageFilterInput;
      paging: PagingInput;
    }
  >(GET_WORKPACKAGES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (user?.crewMember?.persona === PersonaType.GENERAL_FOREMAN || user?.crewMember?.persona === PersonaType.FOREMAN)
      return;
    if (user?.crewMember?.persona === PersonaType.SUPERINTENDENT && user?.crewMember?.id === selectedSuperintendent?.id)
      setDrawerValues({ ...drawerValues, searchScope: true });
    else setDrawerValues({ ...drawerValues, searchScope: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuperintendent]);

  useEffect(() => {
    if (!resetWorkPackagePager) return; //default is false, it will be set to true at the time of changing schedule/ownership of work package
    //After edit ownership/schedule, slider needs to be set to Single and work package needs to be called from page1
    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Single);
    if (planningWpListCursor === 1) {
      //refetch called if work package being changed for ownership/schedule is at page 1.
      refetch();
    } else {
      //cursor set to 1 if work package being changed for ownership/schedule is not at page 1.
      setCursor(1);
    }
    //After completion of change ownership/schedule, this flag will be set to false so that work package list can be refreshed on next change for FM/GF/SUP.
    planningDispatch({
      type: PlanningActionType.ResetWorkPackageSelection,
      payload: { resetWorkPackagePager: false },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetWorkPackagePager, planningDispatch]);

  const filterOwners = useMemo(() => {
    const filterByOwners: WorkPackageOwnerFilterByTerm[] = [];
    //Add field engineer unassigned from drawer values
    if (
      drawerValues &&
      drawerValues?.filterByUnassignedPersona &&
      drawerValues.filterByUnassignedPersona.includes(PersonaType.FIELD_ENGINEER)
    ) {
      filterByOwners.push({ persona: PersonaType.FIELD_ENGINEER, unassigned: true });
    }
    if (selectedSuperintendent?.id == AllSuperintendentId) return filterByOwners; //No owner except FE unassigned from drawer

    if (selectedSuperintendent?.id == UnassignedSuperintendentId) {
      filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, unassigned: true });
    } else if (selectedGeneralForeman?.id == AllGeneralForemanId) {
      filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent?.id });
    } else if (selectedGeneralForeman?.id == UnassignedGeneralForemanId) {
      filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent?.id });
      filterByOwners.push({ persona: PersonaType.GENERAL_FOREMAN, unassigned: true });
    } else if (selectedForeman?.id == AllForemanId) {
      filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent?.id });
      filterByOwners.push({ persona: PersonaType.GENERAL_FOREMAN, crewMemberId: selectedGeneralForeman?.id });
    } else if (selectedForeman?.id == UnassignedForemanId) {
      filterByOwners.push({ persona: PersonaType.SUPERINTENDENT, crewMemberId: selectedSuperintendent?.id });
      filterByOwners.push({ persona: PersonaType.GENERAL_FOREMAN, crewMemberId: selectedGeneralForeman?.id });
      filterByOwners.push({ persona: PersonaType.FOREMAN, unassigned: true });
    } else {
      //All the persona's are selected
      filterByOwners.push({
        persona: PersonaType.SUPERINTENDENT,
        crewMemberId: selectedSuperintendent?.id,
        ownershipType: OwnershipType.PRIMARY,
      });
      filterByOwners.push({
        persona: PersonaType.GENERAL_FOREMAN,
        crewMemberId: selectedGeneralForeman?.id,
        ownershipType: OwnershipType.PRIMARY,
      });
      filterByOwners.push({
        persona: PersonaType.FOREMAN,
        crewMemberId: selectedForeman?.id,
        ownershipType: OwnershipType.PRIMARY,
      });
    }
    return filterByOwners;
  }, [selectedSuperintendent, selectedGeneralForeman, selectedForeman, drawerValues]);

  const defaultWorkPackages = useMemo(() => {
    return get(data, 'workPackages.items', []);
  }, [data]);

  const totalWorkPackages = get(data, 'workPackages.total', 0);
  const totalPages = Math.ceil(totalWorkPackages / planningWorkPackageLimit);
  useEffect(() => {
    //isDefaultFilter added so that work package slider moves to single after reset filters
    if ((planningWorkPackageSlider !== PlanningWorkPackageSlider.Single && !isReplan) || isDefaultFilter) {
      setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Single);
    }
    if (planningWpListCursor != 1) setCursor(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuperintendent, selectedGeneralForeman, selectedForeman, drawerValues, debouncedSearchQuery]);

  const workPackageFilterInput: WorkPackageFilterInput = {
    filterByOwners: filterOwners,
    startDate: drawerValues?.startDate,
    endDate: drawerValues?.endDate,
    searchKeyword: debouncedSearchQuery,
    searchBy: drawerValues?.searchBy ? drawerValues?.searchBy : [{ type: WorkPackageSearchByEnum.WP_NUMBER }],
    filterBy: drawerValues?.dateScope ? drawerValues?.dateScope : WorkPackageFilterByTerm.ALL,
    filterByMilestonesStatusFlags: drawerValues?.filterByMilestone,
    filterByCommodityTypeIds: map(drawerValues?.filterByCommodity, (item: CommodityType) => item.id),
    filterByMilestoneTypeIds: map(drawerValues?.filterByMilestoneTypes, (item: MilestoneType) => item.id),
    // assignedToMe: filterOwners && filterOwners.length == 0 ? false : drawerValues?.searchScope, //When no owners, pass scope as All WorkPackages
    assignedToMe: drawerValues?.searchScope,
    sortBy: drawerValues?.sortBy ? drawerValues?.sortBy : WorkPackageSortByTerm.WP_NUMBER,
    sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortDirection.DESC,
  };

  useEffect(() => {
    if (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) return;

    loadWorkpackages({
      variables: {
        filter: workPackageFilterInput,
        paging: {
          cursor: planningWpListCursor.toString(),
          limit: planningWorkPackageLimit,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workPackageFilterInput.filterByOwners,
    drawerValues,
    debouncedSearchQuery,
    loadWorkpackages,
    planningWpListCursor,
  ]);

  const onDrawerSubmit = useCallback(
    (values: DrawerValuesProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  useEffect(() => {
    if (isReplan) {
      onDrawerSubmit({ ...wpDefaultDrawerValues, filterByMilestone: [MilestoneFilterStatusFlag.EXPIRED] });
    }
    //needed to call the backlog data if user click on replan and move back to other module and come back here
    if (isReplan && drawerValues.filterByMilestone[0] === MilestoneFilterStatusFlag.EXPIRED) {
      setPlanningWorkPackageSlider(PlanningWorkPackageSlider.All);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReplan]);

  useEffect(() => {
    if (isReplan && drawerValues.filterByMilestone[0] === MilestoneFilterStatusFlag.EXPIRED) {
      setPlanningWorkPackageSlider(PlanningWorkPackageSlider.All);
    }
    // checking resetWorkPackagePager false here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected. For other than 'All' value in slider, selected work package list should be empty
    if (!resetWorkPackagePager && !isReplan && planningWorkPackageSlider !== PlanningWorkPackageSlider.All) {
      planningDispatch({
        type: PlanningActionType.ResetWorkPackageSelection,
        payload: { selectedWpList: [] },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (
      planningWorkPackageSlider === PlanningWorkPackageSlider.Single ||
      planningWorkPackageSlider === PlanningWorkPackageSlider.Multiple
    )
      return;
    planningDispatch({
      type: PlanningActionType.AllWorkPackageSelected,
      payload: { selectedWpList: map(defaultWorkPackages, 'id') },
    });
  }, [defaultWorkPackages, planningDispatch, planningWorkPackageSlider]);

  //Start/End dates are getting changed w.r.t timezone while saving drawer values in session storage and hence comparison resulting in false always so converting dates here for comparison
  const isDefaultFilter = isEqual(
    { ...drawerValues, startDate: new Date(drawerValues.startDate), endDate: new Date(drawerValues.endDate) },
    {
      ...wpDefaultDrawerValues,
      startDate: new Date(wpDefaultDrawerValues.startDate),
      endDate: new Date(wpDefaultDrawerValues.endDate),
    }
  );

  return (
    <Fragment>
      <WorkPackageListContainerStyled data-testid="workpackage-list-container">
        <ListSubHeadingStyled>
          <Trans>Work Packages</Trans> {totalWorkPackages > 0 ? `(${totalWorkPackages})` : ``}
        </ListSubHeadingStyled>
        <SearchContainerStyled>
          <Box mx={{ sm: `500` }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
            <Box my={{ sm: '500' }}>
              <SearchField
                iconSize={IconSize.MEDIUM}
                placeholder={t`Search`}
                type="search"
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
              />
              <Box ml={{ sm: '400' }}>
                <SearchCanvasButtonStyled
                  appearance={ButtonAppearance.SECONDARY}
                  isDefaultFilterChange={!isDefaultFilter}
                  size={ButtonSize.MEDIUM}
                  onClick={() => {
                    toggle();
                    setDrawerStatus(!visible);
                  }}
                >
                  <Icon icon="sliders-v" size={IconSize.MEDIUM} fillColor={!isDefaultFilter ? 'white' : ''} />
                </SearchCanvasButtonStyled>
              </Box>
            </Box>

            <Text size="300" weight="600">
              <Trans>Select:</Trans>
            </Text>

            <Box my={{ sm: '400' }} data-testid="select-multiple-tab">
              <SegmentedControl segmentSize={SegmentedControlSize.SMALL} style={{ flexGrow: 1 }}>
                <SegmentedControlOption
                  segmentSize={SegmentedControlSize.SMALL}
                  selected={planningWorkPackageSlider === PlanningWorkPackageSlider.Single}
                  onClick={() => {
                    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Single);
                    planningDispatch({
                      type: PlanningActionType.ResetWorkPackageSelection,
                      payload: { selectedWpList: [] },
                    });
                  }}
                  data-testid={
                    planningWorkPackageSlider === PlanningWorkPackageSlider.Single
                      ? 'single-tab-selected'
                      : 'single-tab-unselected'
                  }
                >
                  <Trans>Single</Trans>
                </SegmentedControlOption>
                <SegmentedControlOption
                  segmentSize={SegmentedControlSize.SMALL}
                  selected={planningWorkPackageSlider === PlanningWorkPackageSlider.Multiple}
                  onClick={() => {
                    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.Multiple);
                    planningDispatch({
                      type: PlanningActionType.ResetWorkPackageSelection,
                      payload: { selectedWpList: [] },
                    });
                  }}
                  data-testid={
                    planningWorkPackageSlider === PlanningWorkPackageSlider.Multiple
                      ? 'multi-tab-selected'
                      : 'multi-tab-unselected'
                  }
                >
                  <Trans>Multiple</Trans>
                </SegmentedControlOption>
                <SegmentedControlOption
                  disabled={true}
                  segmentSize={SegmentedControlSize.SMALL}
                  selected={planningWorkPackageSlider === PlanningWorkPackageSlider.All}
                  onClick={() => {
                    setPlanningWorkPackageSlider(PlanningWorkPackageSlider.All);
                  }}
                  data-testid={
                    planningWorkPackageSlider === PlanningWorkPackageSlider.All
                      ? 'all-tab-selected'
                      : 'all-tab-unselected'
                  }
                >
                  <Trans>All</Trans>
                </SegmentedControlOption>
              </SegmentedControl>
            </Box>
          </Box>
        </SearchContainerStyled>
        <ListStyled>
          {(!called || loading || networkStatus === NetworkStatus.refetch) && (
            <Box style={{ height: '100%' }} data-testid="loading">
              <Box
                flexGrow={{ sm: 1 }}
                alignItems={{ sm: 'center' }}
                flexDirection={{ sm: 'column' }}
                justifyContent={{ sm: 'center' }}
              >
                <Spinner size={SpinnerSize.MEDIUM} />
              </Box>
            </Box>
          )}
          {called && !loading && error && (
            <div data-testid="retry">
              <ErrorRetry refetch={refetch}>
                <Trans>
                  There was a problem loading work package list. Please click the button below to try again.
                </Trans>
              </ErrorRetry>
            </div>
          )}

          {called &&
            !loading &&
            !error &&
            (defaultWorkPackages.length > 0 ? (
              map(defaultWorkPackages, (wp: WorkPackage) => {
                return <ListItem wp={wp} planningWorkPackageSlider={planningWorkPackageSlider} key={wp.id} />;
              })
            ) : (
              <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }}>
                <Heading size="400" weight="600" color="grey.600">
                  <Trans>No work packages found.</Trans>
                </Heading>
                <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                  <Text
                    size="400"
                    family="graphik"
                    color="grey.600"
                    style={{ textAlign: 'center', lineHeight: '1.57' }}
                  >
                    <Trans>Try changing your search or filters to view a work package.</Trans>
                  </Text>
                </Box>
                <FilterIcon style={{ width: '124px', height: '110px' }} />
              </Box>
            ))}
        </ListStyled>
        {!loading && !error && defaultWorkPackages.length > 0 && (
          <PaginationContainerStyled>
            <Pagination
              selectable={false}
              totalPages={totalPages}
              onNext={() => {
                if (planningWpListCursor >= totalPages) return;
                setCursor(planningWpListCursor + 1);
                // resetWorkPackagePager set undefined here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected.
                planningDispatch({
                  type: PlanningActionType.ResetWorkPackageSelection,
                  payload: { resetWorkPackagePager: false },
                });
              }}
              onPrev={() => {
                if (planningWpListCursor <= 1) return;
                setCursor(planningWpListCursor - 1);
                // resetWorkPackagePager set undefined here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected.
                planningDispatch({
                  type: PlanningActionType.ResetWorkPackageSelection,
                  payload: { resetWorkPackagePager: false },
                });
              }}
              currentPage={planningWpListCursor.toString()}
              onPageChange={(page) => {
                setCursor(parseInt(page));
                // resetWorkPackagePager set undefined here to cater the need of selecting all work packages if 'All' was selected in slider and page number changed then next page should have all work packages selected.
                planningDispatch({
                  type: PlanningActionType.ResetWorkPackageSelection,
                  payload: { resetWorkPackagePager: false },
                });
              }}
            />
          </PaginationContainerStyled>
        )}
      </WorkPackageListContainerStyled>
      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
        {visible && <Drawer close={closeDrawer} initialValues={drawerValues} onSubmit={onDrawerSubmit} />}
      </Suspense>
    </Fragment>
  );
};
